import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { usePermissions, MODULES } from '../../context/PermissionContext';
import { useState, useRef, useEffect } from 'react';
import { getAllAnfragen } from '../../api/anfrageApi';

// Persists across mount/unmount cycles — survives page navigation.
// Layout wird bei jedem Route-Wechsel unmountet und neu gemountet (jede Page
// rendert ihr eigenes `<Layout>`). Ohne diesen Cache würde der Indicator
// beim Start jedes Mounts bei Position 0 beginnen.
let lastIndicatorPos = null;
let lastPathname = null;

function Navigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { canView, isAdmin } = usePermissions();
  const ulRef = useRef(null);
  const itemRefs = useRef({});
  const indicatorRef = useRef(null);
  const anfragenPrefetchingRef = useRef(false);
  // Beim Mount soll der Indicator an der ZULETZT bekannten Position stehen —
  // NICHT an der Position des neuen aktiven Tabs (sonst kein Delta für die
  // Transition). Nach dem ersten Paint wird dann zur Ziel-Position animiert.
  const [indicator, setIndicator] = useState(lastIndicatorPos);
  const [shouldAnimate, setShouldAnimate] = useState(!!lastIndicatorPos);

  const isHeykikiOrg = user?.organisation?.orgType === 'HEYKIKI';

  // HEYKIKI-Orgs sehen nur die für sie relevanten Module
  const HEYKIKI_ALLOWED_PATHS = ['/dashboard', '/anfragen', '/kunden', '/kalender', '/plantafel', '/ki-zentrale', '/kostenvoranschlaege', '/rechnungen', '/einstellungen', '/katalog', '/projekte', '/mitarbeiter'];

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', modul: MODULES.DASHBOARD },
    { path: '/ki-zentrale', label: isHeykikiOrg ? 'KiKi Zentrale' : 'KI Zentrale', modul: MODULES.EINSTELLUNGEN },
    { path: '/anfragen', label: isHeykikiOrg ? 'Anrufe' : 'Anfragen', modul: MODULES.ANFRAGEN },
    { path: '/kunden', label: 'Kunden', modul: MODULES.KUNDEN },
    { path: '/kalender', label: 'Kalender', modul: MODULES.TERMINE },
    { path: '/plantafel', label: 'Plantafel', modul: MODULES.PLANTAFEL },
    { path: '/projekte', label: 'Projekte', modul: MODULES.PROJEKTE },
    { path: '/kostenvoranschlaege', label: 'Kostenvoranschläge', modul: MODULES.KOSTENVORANSCHLAEGE },
    { path: '/rechnungen', label: 'Rechnungen', modul: MODULES.RECHNUNGEN },
    { path: '/katalog', label: 'Katalog & Vorlagen', modul: MODULES.KATALOG },
    { path: '/mitarbeiter', label: 'Mitarbeiter', adminOnly: true },
    { path: '/abwesenheiten', label: 'Abwesenheiten', modul: MODULES.ABWESENHEITEN, hideForAdmin: true },
    { path: '/einstellungen', label: 'Einstellungen', modul: MODULES.EINSTELLUNGEN }
  ].filter(item => {
    // HEYKIKI-Orgs: nur Whitelist-Pfade anzeigen
    if (isHeykikiOrg && !HEYKIKI_ALLOWED_PATHS.includes(item.path)) return false;
    if (item.adminOnly) return isAdmin;
    if (item.hideForAdmin && isAdmin) return false;
    if (item.modul) return canView(item.modul);
    return true;
  });

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Anrufe/Anfragen: direkt auf die neueste Anfrage navigieren, ohne den
  // Umweg über /anfragen → AnfragenIndex → <Navigate>. Sonst mountet Layout
  // dreimal (Ursprung → /anfragen → /anfragen/{id}), der Header flickert und
  // die Slide-Animation des Tab-Indicators reißt mitten in der Transition ab.
  // Direkt-Navigation vermeidet den Zwischen-Mount komplett.
  const handleAnfragenClick = async (e) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return;
    if (anfragenPrefetchingRef.current) { e.preventDefault(); return; }
    e.preventDefault();
    anfragenPrefetchingRef.current = true;
    try {
      const anfragen = await getAllAnfragen();
      const newest = Array.isArray(anfragen) && anfragen.length > 0
        ? [...anfragen].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]
        : null;
      navigate(newest ? `/anfragen/${newest.id}` : '/anfragen');
    } catch {
      navigate('/anfragen');
    } finally {
      anfragenPrefetchingRef.current = false;
    }
  };

  // Position berechnen über offsetLeft/offsetWidth — stabil gegenüber
  // Scroll-Position und Subpixel-Rundungen.
  const measureActiveTab = () => {
    const ul = ulRef.current;
    if (!ul) return null;
    let activePath = null;
    for (const item of navItems) {
      if (location.pathname === item.path || location.pathname.startsWith(item.path + '/')) {
        activePath = item.path;
        break;
      }
    }
    const el = activePath ? itemRefs.current[activePath] : null;
    if (!el) return null;
    return { left: el.offsetLeft, width: el.offsetWidth };
  };

  // Indicator-Animation bei Route-Wechsel.
  //
  // Kernproblem: Jede Page rendert ihr eigenes `<Layout>`, also wird Navigation
  // bei jedem Route-Wechsel UNMOUNTED und neu GEMOUNTED. Eine CSS-Transition
  // braucht aber zwei Paints auf DEMSELBEN DOM-Element — Paint 1 mit alter
  // Position, Paint 2 mit neuer. Beim Mount muss der Browser also zuerst die
  // alte Cache-Position painten, bevor wir die neue setzen.
  //
  // Lösung: Wir nutzen `requestAnimationFrame` doppelt, damit der Browser
  // dazwischen zwingend mindestens einen Paint durchführt. Zusätzlich lesen
  // wir die Position SYNCHRON nach Render, weil React 18 sonst Commits
  // zusammenbatchen kann.
  useEffect(() => {
    const pathChanged = lastPathname !== location.pathname;
    lastPathname = location.pathname;

    // Fall 1: Kein echter Pfad-Wechsel (z. B. initialer Mount auf gleicher
    // Route, oder Neumount nach Auto-Redirect wie AnfragenIndex → Anfrage-
    // Detail). Direkt Position synchronisieren ohne Transition-Trick.
    if (!pathChanged) {
      const pos = measureActiveTab();
      if (pos) {
        setIndicator(pos);
        lastIndicatorPos = pos;
      }
      if (!shouldAnimate) setShouldAnimate(true);
      return;
    }

    // Fall 2: Echter Pfad-Wechsel → Two-Frame-Trick für saubere Animation.
    // Paint 1: Indicator rendert an Cache-Position (alter Tab).
    // RAF → Browser painted.
    // RAF → Neue Position setzen.
    // Paint 2: Browser sieht neuen Zielwert → CSS-Transition feuert.
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        const pos = measureActiveTab();
        if (pos) {
          setIndicator(pos);
          lastIndicatorPos = pos;
        }
        if (!shouldAnimate) setShouldAnimate(true);
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, navItems.length]);

  useEffect(() => {
    const onResize = () => {
      const pos = measureActiveTab();
      if (pos) {
        setIndicator(pos);
        lastIndicatorPos = pos;
      }
    };
    window.addEventListener('resize', onResize);
    // Auch Fonts-Loading abwarten — Text-Breite ändert sich, sobald die
    // Webfont-Ressourcen da sind; ohne das sitzt der Indicator bei frischem
    // Load initial an der falschen Breite.
    if (document.fonts?.ready) {
      document.fonts.ready.then(() => {
        const pos = measureActiveTab();
        if (pos) {
          setIndicator(pos);
          lastIndicatorPos = pos;
        }
      }).catch(() => {});
    }
    return () => window.removeEventListener('resize', onResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // WICHTIG für /anfragen-Tab: AnfragenIndex redirected auto zu /anfragen/{id}.
  // Dabei unmountet Layout/Navigation MITTEN in der laufenden CSS-Transition.
  // Ohne Snapshot hätte der neue Mount `lastIndicatorPos = Ziel-Position`
  // (vom useEffect der unmountenden Instanz), dadurch startet der neue Mount
  // bereits an der Ziel-Position → kein Delta → keine sichtbare Animation.
  //
  // Lösung: Bei Unmount die TATSÄCHLICH gerade sichtbare Position (mid-
  // animation) aus den Computed Styles lesen und als `lastIndicatorPos`
  // speichern. So setzt die neu gemountete Navigation ihren Indicator genau
  // dort fort, wo die alte aufgehört hat — und animiert smooth weiter zur
  // Zielposition.
  useEffect(() => {
    return () => {
      const el = indicatorRef.current;
      if (!el) return;
      try {
        const computed = window.getComputedStyle(el);
        const transform = computed.transform;
        // Format: "matrix(a, b, c, d, tx, ty)" oder "none"
        if (transform && transform !== 'none') {
          const match = transform.match(/matrix\(([^)]+)\)/);
          if (match) {
            const values = match[1].split(',').map(s => parseFloat(s.trim()));
            // matrix(a,b,c,d,tx,ty): tx = values[4]
            const tx = values[4];
            const w = parseFloat(computed.width);
            if (!Number.isNaN(tx) && !Number.isNaN(w) && w > 0) {
              lastIndicatorPos = { left: tx, width: w };
            }
          }
        }
      } catch { /* ignore */ }
    };
  }, []);

  return (
    <nav className="bg-surface border-b border-border desktop-only">
      <div className="container mx-auto px-4 lg:px-6">
        <ul ref={ulRef} className="flex gap-1 lg:gap-4 xl:gap-6 overflow-x-auto scrollbar-hide relative">
          {navItems.map((item) => (
            <li key={item.path} className="flex-shrink-0">
              <Link
                ref={el => { itemRefs.current[item.path] = el; }}
                to={item.path}
                onClick={item.path === '/anfragen' ? handleAnfragenClick : undefined}
                className={`block py-3 lg:py-4 text-sm lg:text-base whitespace-nowrap transition-colors relative ${
                  isActive(item.path)
                    ? 'text-primary font-medium'
                    : 'text-text-muted hover:text-text'
                }`}
              >
                {item.label}
              </Link>
            </li>
          ))}
          <div
            ref={indicatorRef}
            className="absolute bottom-0 h-0.5 bg-primary rounded-full pointer-events-none"
            style={{
              left: 0,
              transform: `translateX(${indicator?.left ?? 0}px)`,
              width: indicator?.width ?? 0,
              opacity: indicator ? 1 : 0,
              // Material-Standard-Easing (easeInOut) fühlt sich auf langen
              // Distanzen natürlicher an als die frühere easeOut-Kurve, die
              // bei großen Sprüngen (z. B. Projekte → Anrufe) „abbremst".
              transition: shouldAnimate
                ? 'transform 320ms cubic-bezier(0.4, 0, 0.2, 1), width 320ms cubic-bezier(0.4, 0, 0.2, 1), opacity 150ms ease'
                : 'none',
              willChange: 'transform, width'
            }}
          />
        </ul>
      </div>
    </nav>
  );
}

export default Navigation;
